import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useAuth } from '../lib/auth/auth-context';
import { forgotPasswordSchema, type ForgotPasswordInput } from '../lib/auth/schemas';
import { friendlyAuthError } from '../lib/auth/errors';
import { AuthShell, ErrorAlert, InfoAlert } from '../components/auth/AuthShell';
import { Field } from '../components/ui/Field';
import { Button } from '../components/ui/Button';

export const Route = createFileRoute('/forgot-password')({
  component: ForgotPasswordPage,
});

function ForgotPasswordPage(): JSX.Element {
  const { requestPasswordReset } = useAuth();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ForgotPasswordInput>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: { email: '' },
  });

  const onSubmit = handleSubmit(async (values) => {
    setSubmitError(null);
    const redirectTo = `${window.location.origin}/reset-password`;
    const { error } = await requestPasswordReset(values.email, redirectTo);
    if (error) {
      setSubmitError(friendlyAuthError('reset-request', error));
      return;
    }
    // Always show the same confirmation regardless of whether the email
    // exists — avoids enumeration of pharmacy staff emails.
    setSubmitted(true);
  });

  return (
    <AuthShell
      title="Reset your password"
      subtitle="We'll email you a link to set a new one."
      footer={
        <Link to="/login" className="text-primary hover:underline">
          Back to sign in
        </Link>
      }
    >
      {submitted ? (
        <InfoAlert>
          If that email is registered, we&apos;ve sent a reset link. Check your inbox (and spam) for
          a message from LYN Rx.
        </InfoAlert>
      ) : (
        <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
          {submitError ? <ErrorAlert>{submitError}</ErrorAlert> : null}
          <Field
            label="Email"
            type="email"
            autoComplete="email"
            autoFocus
            error={errors.email?.message}
            {...register('email')}
          />
          <Button type="submit" loading={isSubmitting} fullWidth>
            Send reset link
          </Button>
        </form>
      )}
    </AuthShell>
  );
}
